import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Sparkles, Trash2, X } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from '../PlatformIcon';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import type { UpcomingSocialPostSnippet } from '../../hooks/useUpcomingSocialPostsSnippet';
import { useImageViewer } from '../ImageViewerProvider';
import ScheduledTimeEditor from '../marketing/ScheduledTimeEditor';
import { postPlacementLabel } from '../../lib/postPlacement';
import { useFinishVideoCaption } from '../../hooks/useFinishVideoCaption';

interface ControlCentreSocialPostPreviewModalProps {
  open: boolean;
  businessId: string;
  post: UpcomingSocialPostSnippet | null;
  onClose: () => void;
  onChanged: () => void;
}

function scheduleLabel(iso: string | null | undefined): string {
  if (!iso) return 'Not scheduled yet';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ControlCentreSocialPostPreviewModal({
  open,
  businessId,
  post,
  onClose,
  onChanged,
}: ControlCentreSocialPostPreviewModalProps) {
  const { open: openImageViewer } = useImageViewer();
  const [content, setContent] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !post) return;
    setContent(post.content || '');
    setDirty(false);
    setScheduledFor(post.scheduled_for ?? null);
  }, [open, post]);

  // A bot may have staged a video with text that this app still has to draw
  // into the file. Do it here if the chat card hasn't already.
  const needsFinish = !!(open && (post?.video_caption_pending || post?.video_thumbnail_pending));
  const finish = useFinishVideoCaption({
    businessId,
    draftId: open && post?.status === 'pending' ? post.id : null,
    pending: needsFinish,
  });
  const finishedUrlRef = useRef<string | null>(null);
  // A finished video fires a click that lands on the backdrop and closes the modal.
  const ignoreBackdropCloseUntil = useRef(0);
  const resettingVideo = useRef(false);
  const resetVideo = (video: HTMLVideoElement) => {
    if (resettingVideo.current) return;
    resettingVideo.current = true;
    ignoreBackdropCloseUntil.current = Date.now() + 600;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      /* seeking can fail before the file has metadata */
    }
    window.setTimeout(() => {
      resettingVideo.current = false;
    }, 600);
  };
  useEffect(() => {
    if (finish.status === 'done' && finish.mediaUrl && finishedUrlRef.current !== finish.mediaUrl) {
      finishedUrlRef.current = finish.mediaUrl;
      if (post?.video_caption_pending || post?.video_thumbnail_pending) onChanged();
    }
  }, [finish, post?.video_caption_pending, post?.video_thumbnail_pending, onChanged]);

  if (!open || !post) return null;

  const isVideo =
    post.is_video === true || /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(post.image_url || '');
  const videoUrl = finish.status === 'done' && finish.mediaUrl ? finish.mediaUrl : post.image_url || '';
  const finishing = !!(post.video_caption_pending || post.video_thumbnail_pending) && finish.status !== 'done' && finish.status !== 'error';

  const isAi = (post.source || 'ai') === 'ai';
  const isPublished = post.status === 'published';
  const label =
    post.platform === 'instagram' ? 'Instagram' : post.platform === 'linkedin' ? 'LinkedIn' : 'Facebook';
  const Icon =
    post.platform === 'instagram' ? InstagramIcon : post.platform === 'linkedin' ? LinkedInIcon : FacebookIcon;
  const isInstagram = post.platform === 'instagram';
  const placement = postPlacementLabel(post.platform, post.post_format);
  const origin = isAi ? 'AI post' : 'Your post';
  const busy = saving || rejecting;
  const maxLen = post.platform === 'linkedin' ? 3000 : 2200;

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error('Caption is required');
      return;
    }
    if (trimmed === (post.content || '').trim()) {
      toast.success('No changes to save');
      setDirty(false);
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'update',
          businessId,
          draftId: post.id,
          connectionId: post.connection_id,
          platform: post.platform,
          content: trimmed,
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to update caption',
        );
      }
      const next = String(data?.draft?.content || trimmed).trim();
      setContent(next);
      setDirty(false);
      toast.success('Caption updated');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update caption');
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (nextIso: string) => {
    const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
      body: {
        action: 'update',
        businessId,
        draftId: post.id,
        connectionId: post.connection_id,
        platform: post.platform,
        scheduledFor: nextIso,
      },
    });
    if (error || data?.error) {
      throw new Error(
        data?.error || (await invokeErrorMessage(error, data)) || 'Failed to update time',
      );
    }
    setScheduledFor(String(data?.draft?.scheduled_for || nextIso));
    toast.success('Time updated');
    onChanged();
  };

  const handleDeletePublished = async () => {
    if (
      !confirm(
        `Delete this ${label} post? It will be removed from ${label} and from your calendar. This cannot be undone.`,
      )
    ) {
      return;
    }
    const ayrsharePostId = post.ayrshare_post_id;
    if (!ayrsharePostId) {
      toast.error(`This post cannot be removed from ${label} because it was not published from Nexus.`);
      return;
    }
    const functionName =
      post.platform === 'instagram'
        ? 'ayrshare-instagram-posts'
        : post.platform === 'linkedin'
          ? 'ayrshare-linkedin-posts'
          : 'ayrshare-facebook-posts';
    setRejecting(true);
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: {
          action: 'delete',
          businessId,
          connectionId: post.connection_id,
          ayrsharePostId,
          nexusHistoryId: post.id,
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to delete post',
        );
      }
      toast.success('Post deleted');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete post');
    } finally {
      setRejecting(false);
    }
  };

  const handleReject = async () => {
    if (
      !confirm(
        isAi
          ? `Reject this AI ${label} post? It will be removed from the upcoming queue.`
          : `Discard this staged ${label} post?`,
      )
    ) {
      return;
    }
    setRejecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'reject',
          businessId,
          draftId: post.id,
          connectionId: post.connection_id,
          platform: post.platform,
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to discard post',
        );
      }
      toast.success(isAi ? 'Post rejected' : 'Post discarded');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to discard post');
    } finally {
      setRejecting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100200] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (Date.now() < ignoreBackdropCloseUntil.current) return;
          if (!busy) onClose();
        }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-labelledby="social-post-preview-title"
        className="relative w-full sm:max-w-xl bg-white sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h2
              id="social-post-preview-title"
              className="text-base font-semibold text-gray-900 inline-flex items-center gap-2"
            >
              <Icon className="h-5 w-5 shrink-0" />
              {label} post
            </h2>
            {!isPublished && scheduledFor ? (
              <ScheduledTimeEditor
                className="mt-0.5"
                scheduledFor={scheduledFor}
                disabled={busy}
                onSave={handleReschedule}
                suffix={`${placement} · ${origin}`}
              />
            ) : (
              <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                {isPublished
                  ? `Published · ${scheduleLabel(post.created_at)}`
                  : scheduleLabel(post.scheduled_for)}
                {` · ${placement} · ${origin}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {post.image_url && isVideo && (
            <div className="relative flex w-full items-center justify-center rounded-xl overflow-hidden border border-gray-200 bg-black">
              <video
                key={videoUrl}
                src={videoUrl}
                controls={!finishing}
                playsInline
                preload="metadata"
                className="w-full max-h-[min(62vh,720px)] object-contain"
                onClick={(e) => e.stopPropagation()}
                onEnded={(e) => {
                  e.stopPropagation();
                  resetVideo(e.currentTarget);
                }}
              />
              <span className="absolute top-2 left-2 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white">
                {post.post_format === 'reel' ? 'Reel' : post.post_format === 'story' ? 'Story' : 'Video'}
              </span>
              {finishing && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-sm font-medium">
                    {post.video_caption_pending ? 'Adding text to the video…' : 'Choosing a cover…'}
                    {post.video_caption_pending && finish.status === 'baking' ? ` ${Math.round(finish.progress * 100)}%` : ''}
                  </span>
                </div>
              )}
              {(post.video_caption_pending || post.video_thumbnail_pending) && finish.status === 'error' && (
                <div className="absolute inset-x-0 bottom-0 bg-red-600/90 px-3 py-1.5 text-[12px] font-medium text-white">
                  {post.video_caption_pending ? 'The text couldn’t be added' : 'The cover couldn’t be chosen'}: {finish.message}
                </div>
              )}
            </div>
          )}
          {post.image_url && !isVideo && (
            <button
              type="button"
              onClick={() =>
                openImageViewer({
                  images: [
                    {
                      src: post.image_url!,
                      caption: `${label} post`,
                    },
                  ],
                  index: 0,
                })
              }
              className="relative flex w-full items-center justify-center rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
              title="Click to view full size"
            >
              <img
                src={post.image_url}
                alt=""
                className="w-full max-h-[min(62vh,720px)] object-contain"
                referrerPolicy="no-referrer"
              />
              {post.image_count > 1 ? (
                <span className="absolute top-2 right-2 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white">
                  {post.image_count} photos
                </span>
              ) : null}
            </button>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-500" htmlFor="social-post-caption">
                Caption
              </label>
              <span className="text-[11px] text-gray-400">
                {content.length}/{maxLen}
              </span>
            </div>
            {isPublished ? (
              <p className="w-full text-sm rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 whitespace-pre-wrap text-gray-800">
                {content || '—'}
              </p>
            ) : (
              <textarea
                id="social-post-caption"
                value={content}
                onChange={(e) => {
                  setContent(e.target.value.slice(0, maxLen));
                  setDirty(true);
                }}
                rows={isInstagram ? 8 : 6}
                disabled={busy}
                className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60 whitespace-pre-wrap"
              />
            )}
            {!isPublished && isInstagram ? (
              <p className="text-[11px] text-gray-400 mt-1.5">
                Tip: keep Instagram captions link-free. Links are stripped on save.
              </p>
            ) : null}
          </div>

          {isPublished ? (
            <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5 font-medium">
              Published — this post is live on {label}
            </p>
          ) : isAi ? (
            <p className="text-xs text-blue-700 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Generated by AI. It publishes on its own at the time above; edit the caption here first if you like.
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200 shrink-0">
          {isPublished ? (
            <button
              type="button"
              onClick={() => void handleDeletePublished()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
            >
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleReject()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
            >
              {rejecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {isAi ? 'Reject' : 'Discard'}
            </button>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3.5 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              Close
            </button>
            {!isPublished ? (
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={busy || !dirty || !content.trim()}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save caption
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
